"use client";

import { useTranslations } from "next-intl";
import * as React from "react";

import { useAviFilterStore } from "@/store/useAviFilterStore";

import type { FilterTag } from "../components/filterTags";

export function useAviFilterTags() {
  const tGlossary = useTranslations("glossary");
  const { applied, removeAppliedFilter, reset } = useAviFilterStore();

  const tags = React.useMemo<FilterTag[]>(() => {
    const result: FilterTag[] = [];

    applied.deadline_status?.forEach((status) => {
      result.push({
        label: tGlossary(`deadlineStatus.${status}`),
        onRemove: () => {
          const next = applied.deadline_status!.filter((s) => s !== status);
          removeAppliedFilter({ deadline_status: next.length ? next : undefined });
        },
      });
    });

    applied.workflow_status?.forEach((status) => {
      result.push({
        label: tGlossary(`workflowStatus.${status}`),
        onRemove: () => {
          const next = applied.workflow_status!.filter((s) => s !== status);
          removeAppliedFilter({ workflow_status: next.length ? next : undefined });
        },
      });
    });

    applied.pillar?.forEach((pillar) => {
      result.push({
        label: pillar.toUpperCase(),
        onRemove: () => {
          const next = applied.pillar!.filter((current) => current !== pillar);
          removeAppliedFilter({ pillar: next.length ? next : undefined });
        },
      });
    });

    if (applied.standard_acronym) {
      result.push({
        label: applied.standard_acronym,
        onRemove: () => removeAppliedFilter({ standard_acronym: undefined }),
      });
    }

    if (applied.project_code) {
      result.push({
        label: applied.project_label || applied.project_code,
        onRemove: () =>
          removeAppliedFilter({
            project_code: undefined,
            site_name: undefined,
            asset_names: undefined,
          }),
      });
    }

    if (applied.site_name) {
      result.push({
        label: applied.site_name,
        onRemove: () => removeAppliedFilter({ site_name: undefined, asset_names: undefined }),
      });
    }

    applied.asset_names?.forEach((name, idx) => {
      result.push({
        label: name,
        onRemove: () => {
          const nextNames = applied.asset_names!.filter((_, i) => i !== idx);
          const nextIds = applied.asset_ids!.filter((_, i) => i !== idx);
          removeAppliedFilter({
            asset_names: nextNames.length ? nextNames : undefined,
            asset_ids: nextIds.length ? nextIds : undefined,
          });
        },
      });
    });

    return result;
  }, [applied, removeAppliedFilter, tGlossary]);

  return { tags, reset };
}
